import { useRef, useState } from 'react'
import { Button, Card, Col, Form, FormInstance, Row, message } from 'antd'
import { Flex } from '@weareberyl/design-system'
import type { DeepPartial } from '@apollo/client/utilities'
import type { FormProviderProps } from 'antd/lib/form/context'

import type {
  BaseFormValues,
  ConfigFormValues,
  DetailsFormValues,
} from './formTypes'
import {
  type SchemeProductInput,
  VehicleCategory,
  type SchemeProduct,
  ProductType,
} from '~/gql/generated/graphql'
import ProductPricingConfigForm from './ProductPricingConfigForm'
import { transformCurrencyFields } from './utils'
import ProductDetailsForm from './ProductDetailsForm'
import { tailLayout } from '~/constants/forms'
import PeriodPassDetailsForm from './PeriodPassDetailsForm'

export type ProductFormProps = {
  loading?: boolean
  initialValues?: DeepPartial<SchemeProduct>
  onFinish: (schemeProduct: SchemeProductInput) => void
}
const ProductForm = ({
  initialValues,
  loading,
  onFinish,
}: ProductFormProps) => {
  // Define refs for nessted forms for imperative control from this scope (reset button)
  const baseFormRef = useRef<FormInstance<BaseFormValues>>(null)
  const configsFormRef = useRef<FormInstance<ConfigFormValues> | null>(null)
  const detailsFormRef = useRef<FormInstance<DetailsFormValues>>(null)

  // Track currently selected productType in the base form
  // Set default value if this is an edit form where product_type would be present in initialValues
  const [productType, setProductType] = useState<ProductType | undefined>(
    initialValues?.product_type,
  )

  // Product type should always be set, if its edit or create
  const isPeriodPass = productType === ProductType.period_pass

  // Extract FormattedAmount.amount for formatted amounts in initialValues
  const transformedInitiaValues: SchemeProductInput = transformCurrencyFields(
    initialValues ?? {},
  )

  // Set initial value for enabled field for each VehicleCategory if there is a product config for it
  for (const category in VehicleCategory) {
    if (transformedInitiaValues.configs_v3[category]) {
      transformedInitiaValues.configs_v3[category].enabled = true
    }
  }

  const onFormFinish: FormProviderProps['onFormFinish'] = async (
    name,
    { forms },
  ) => {
    // submitForm is just a submitable form, when it's submitted, validate and combine
    // the fields from the details and config forms
    if (name === 'submitForm') {
      let results: [
        BaseFormValues,
        ConfigFormValues,
        DetailsFormValues | undefined,
      ]
      try {
        // Run validations concurrently to show all errors at once;
        // sequential validation would stop early and hide later errors.
        results = await Promise.all([
          forms.productForm.validateFields(),
          forms.configs_v3.validateFields(),
          forms.details?.validateFields() ?? Promise.resolve(undefined),
        ])
      } catch {
        // No need to handle, form will display error messages
        return
      }

      const [base, configs, details] = results

      for (const category in VehicleCategory) {
        if (!configs.configs_v3[category].enabled) {
          // Remove placeholder config entries for non-populated vehicle categories before sending
          delete configs.configs_v3[category]
        } else {
          // Remove the enabled field, that's not used by the mutation
          delete configs.configs_v3[category].enabled
        }
      }

      if (Object.keys(configs.configs_v3).length === 0) {
        message.error('Please set configs for at least one vehicle type')
        return
      }
      const data = { ...base, ...configs, ...details }

      onFinish(data)
    }
  }

  const onFormChange: FormProviderProps['onFormChange'] = (name, info) => {
    // Look for changes in the product_type field, to display different stuff
    // at this level, which is outside the form context, so can't use useWatch
    if (name === 'productForm') {
      info.changedFields.forEach(fieldData => {
        if (fieldData.name[0] === 'product_type') {
          setProductType(fieldData.value)
        }
      })
    }
  }

  const onReset = () => {
    baseFormRef.current?.resetFields()
    configsFormRef.current?.resetFields()
    detailsFormRef.current?.resetFields()
  }

  return (
    <Form.Provider onFormFinish={onFormFinish} onFormChange={onFormChange}>
      <Row gutter={[16, 16]}>
        <Col span={isPeriodPass ? 12 : 24}>
          <ProductDetailsForm
            loading={loading}
            initialValues={transformedInitiaValues}
            formRef={baseFormRef}
          />
        </Col>
        {isPeriodPass && (
          <Col span={12}>
            <PeriodPassDetailsForm
              loading={loading}
              initialValues={transformedInitiaValues}
              formRef={detailsFormRef}
            />
          </Col>
        )}
      </Row>

      <Card title="Vehicle pricing config">
        <Form<ConfigFormValues>
          layout="vertical"
          name="configs_v3"
          initialValues={transformedInitiaValues}
          ref={configsFormRef}
        >
          {productType && (
            <Row gutter={[10, 20]}>
              {Object.keys(VehicleCategory).map(category => {
                const vehicleCategory = VehicleCategory[category]
                return (
                  <Col
                    xs={{ span: 24 }}
                    md={{ span: 12 }}
                    lg={{ span: 6 }}
                    key={category}
                  >
                    <ProductPricingConfigForm
                      productType={productType}
                      category={vehicleCategory}
                    />
                  </Col>
                )
              })}
            </Row>
          )}
        </Form>
      </Card>

      <Form name="submitForm">
        <Card>
          <Form.Item {...tailLayout}>
            <Flex style={{ gap: '10px' }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save
              </Button>
              <Button type="dashed" onClick={onReset}>
                Reset
              </Button>
            </Flex>
          </Form.Item>
        </Card>
      </Form>
    </Form.Provider>
  )
}

export default ProductForm
